import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { authStore, type components } from "@qorakul/shared/api";
import { api } from "./api.ts";
import { retryAfterSeconds } from "./retry-after.ts";

export type CurrentUser = components["schemas"]["CurrentUserResponse"];

interface AuthState {
  user: CurrentUser | null;
  /** True until the first silent refresh attempt has finished. */
  initializing: boolean;
  login(userName: string, password: string): Promise<{ ok: true } | { ok: false; status: number; retryAfterSeconds?: number }>;
  logout(): Promise<void>;
  hasPermission(code: string): boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [initializing, setInitializing] = useState(true);

  // On load, try the HttpOnly refresh cookie once so a page reload keeps the session.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await api.POST("/auth/refresh");
      if (cancelled) return;
      if (data) {
        authStore.set(data.accessToken);
        setUser(data.user);
      }
      setInitializing(false);
    })();
    const onUnauthenticated = () => setUser(null);
    window.addEventListener("qorakul:unauthenticated", onUnauthenticated);
    return () => {
      cancelled = true;
      window.removeEventListener("qorakul:unauthenticated", onUnauthenticated);
    };
  }, []);

  const login = useCallback<AuthState["login"]>(async (userName, password) => {
    const { data, error, response } = await api.POST("/auth/login", { body: { userName, password } });
    if (data) {
      authStore.set(data.accessToken);
      setUser(data.user);
      return { ok: true };
    }
    const retry = retryAfterSeconds(error, response.headers);
    return retry === undefined ? { ok: false, status: response.status } : { ok: false, status: response.status, retryAfterSeconds: retry };
  }, []);

  const logout = useCallback(async () => {
    await api.POST("/auth/logout");
    authStore.set(null);
    setUser(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, initializing, login, logout, hasPermission: (code) => user?.permissions.includes(code) ?? false }),
    [user, initializing, login, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
